import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Modal } from 'react-bootstrap';
import { Analysis } from '../../constants/analysisTypes';
import { useNavigate } from 'react-router-dom';
import { formatTimestamp } from '../../utils/dateFormatters';

interface ListAnalysesProps {
  analyses: Analysis[];
  onCreateAnalysis: () => void;
  onDeleteAnalysis: (analysis_id: string) => Promise<void>;
  onRefresh: () => Promise<void>;
}

const getStatusBadgeVariant = (status: Analysis['status']) => {
  switch (status) {
    case 'pending': return 'warning';
    case 'in_progress': return 'info';
    case 'completed': return 'success';
    case 'failed': return 'danger';
    default: return 'secondary';
  }
};

type SortKey = 'analysis_id' | 'name' | 'type' | 'created_at' | 'completed_at' | 'status';
type SortDir = 'asc' | 'desc';

const ListAnalyses: React.FC<ListAnalysesProps> = ({ 
  analyses, 
  onCreateAnalysis, 
  onDeleteAnalysis,
  onRefresh 
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState<Analysis | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const navigate = useNavigate();

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortIndicator = (key: SortKey) => {
    if (key !== sortKey) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const sortedAnalyses = [...analyses].sort((a, b) => {
    const av = (a as any)[sortKey] ?? '';
    const bv = (b as any)[sortKey] ?? '';
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  useEffect(() => {
    const hasRunningAnalyses = analyses.some(a => 
      a.status === 'pending' || a.status === 'in_progress'
    );

    let interval: ReturnType<typeof setInterval> | null = null;
    
    if (hasRunningAnalyses) {
      interval = setInterval(onRefresh, 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [analyses, onRefresh]);

  const handleDelete = async () => {
    if (analysisToDelete) {
      await onDeleteAnalysis(analysisToDelete.analysis_id);
      setShowDeleteConfirm(false);
      setAnalysisToDelete(null);
    }
  };

  const handleView = (analysis: Analysis) => {
    navigate(`/cases/${analysis.case_id}/analyses/${analysis.analysis_id}`);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <h3>Analyses</h3>
        <Button variant="primary" onClick={onCreateAnalysis}>
          Create Analysis
        </Button>
      </div>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('analysis_id')}>Analysis ID{sortIndicator('analysis_id')}</th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('type')}>Type{sortIndicator('type')}</th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('created_at')}>Started{sortIndicator('created_at')}</th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('completed_at')}>Completed{sortIndicator('completed_at')}</th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {analyses.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center">No analyses available</td>
            </tr>
          ) : (
            sortedAnalyses.map((analysis) => (
              <tr key={analysis.analysis_id}>
                <td>{analysis.analysis_id.slice(0, 8)}</td>
                <td>{analysis.name}</td>
                <td>{analysis.type}</td>
                <td>{formatTimestamp(analysis.created_at)}</td>
                <td>{analysis.completed_at ? formatTimestamp(analysis.completed_at) : '-'}</td>
                <td>
                  <Badge bg={getStatusBadgeVariant(analysis.status)}>
                    {analysis.status}
                  </Badge>
                </td>
                <td>
                  <Button 
                    variant="link" 
                    size="sm"
                    onClick={() => handleView(analysis)}
                    className="me-2"
                  >
                    View
                  </Button>
                  <Button 
                    variant="link" 
                    size="sm"
                    className="text-danger"
                    onClick={() => {
                      setAnalysisToDelete(analysis);
                      setShowDeleteConfirm(true);
                    }}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the analysis "{analysisToDelete?.name}" (id={analysisToDelete?.analysis_id})?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ListAnalyses; 